import React, { useEffect, useState } from 'react';
import { Modal, Input, Button, Tag, Tooltip, message, Spin } from 'antd';
import { EditOutlined, DeleteOutlined, SaveOutlined, QuestionCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { isValidGlossaryTerm } from '../utils/glossaryValidation';

interface DemoAlwaysTranslateModalProps {
  isVisible: boolean;
  onClose: () => void;
  currentLanguagePair: string;
  alwaysTranslateWords: { [pair: string]: string[] };
  setAlwaysTranslateWords: React.Dispatch<React.SetStateAction<{ [pair: string]: string[] }>>;
}

const DemoAlwaysTranslateModal: React.FC<DemoAlwaysTranslateModalProps> = ({
  isVisible,
  onClose,
  currentLanguagePair,
  alwaysTranslateWords,
  setAlwaysTranslateWords,
}) => {
  const [newSourceWord, setNewSourceWord] = useState('');
  const [newTargetWord, setNewTargetWord] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedSourceWord, setEditedSourceWord] = useState('');
  const [editedTargetWord, setEditedTargetWord] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isVisible) {
      setNewSourceWord('');
      setNewTargetWord('');
      setEditingIndex(null);
      setEditedSourceWord('');
      setEditedTargetWord('');
    }
  }, [isVisible]);

  const isValidInput = isValidGlossaryTerm;

  // Simulate async "save" for UX parity (no backend in demo)
  const withSimulatedSave = async (fn: () => void) => {
    setIsLoading(true);
    try {
      await new Promise((res) => setTimeout(res, 350));
      fn();
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddWord = async () => {
    if (!currentLanguagePair) {
      message.error('No language pair selected.');
      return;
    }
    const src = newSourceWord.trim();
    const tgt = newTargetWord.trim();
    if (!src || !tgt) {
      message.error('Please enter both source and target words or phrases.');
      return;
    }
    if (!isValidInput(src) || !isValidInput(tgt)) {
      message.error('Please enter valid words/phrases (letters, numbers, spaces, hyphens, apostrophes).');
      return;
    }
    await withSimulatedSave(() => {
      const pair = `${src} | ${tgt}`;
      const updated = [...(alwaysTranslateWords[currentLanguagePair] || []), pair];
      setAlwaysTranslateWords((prev) => ({ ...prev, [currentLanguagePair]: updated }));
      setNewSourceWord('');
      setNewTargetWord('');
      message.success('Added.');
    });
  };

  const handleEditWord = (index: number) => {
    const pair = (alwaysTranslateWords[currentLanguagePair] || [])[index];
    const [src, tgt] = (pair || '').split(' | ').map((s) => (s ?? '').trim());
    setEditingIndex(index);
    setEditedSourceWord(src || '');
    setEditedTargetWord(tgt || '');
  };

  const handleSaveEdit = async () => {
    if (editingIndex === null) return;
    const src = editedSourceWord.trim();
    const tgt = editedTargetWord.trim();
    if (!src || !tgt) {
      message.error('Please enter both source and target words or phrases.');
      return;
    }
    if (!isValidInput(src) || !isValidInput(tgt)) {
      message.error('Please enter valid words/phrases (letters, numbers, spaces, hyphens, apostrophes).');
      return;
    }
    await withSimulatedSave(() => {
      const updated = [...(alwaysTranslateWords[currentLanguagePair] || [])];
      updated[editingIndex] = `${src} | ${tgt}`;
      setAlwaysTranslateWords((prev) => ({ ...prev, [currentLanguagePair]: updated }));
      setEditingIndex(null);
      message.success('Saved.');
    });
  };

  const handleRemoveWord = async (index: number) => {
    await withSimulatedSave(() => {
      const updated = (alwaysTranslateWords[currentLanguagePair] || []).filter((_, i) => i !== index);
      setAlwaysTranslateWords((prev) => ({ ...prev, [currentLanguagePair]: updated }));
      message.success('Removed.');
    });
  };

  const handleClose = () => {
    setNewSourceWord('');
    setNewTargetWord('');
    setEditingIndex(null);
    setEditedSourceWord('');
    setEditedTargetWord('');
    onClose();
  };

  return (
    <Modal
      title={
        <span>
          Always Translate for {currentLanguagePair || '<none>'}
          <Tooltip title="The AI will always translate the source text to the specified target text">
            <QuestionCircleOutlined style={{ marginLeft: 8, cursor: 'pointer', color: '#1677ff' }} />
          </Tooltip>
        </span>
      }
      visible={isVisible}
      onCancel={handleClose}
      footer={[<Button key="close" onClick={handleClose}>Close</Button>]}
      width={700}
    >
      <Spin spinning={isLoading}>
        <div style={{ background: '#f7f7f7', padding: 15, borderRadius: 6, marginBottom: 15 }}>
          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 5 }}>
              <strong>Source Word/Phrase:</strong>
            </label>
            <Input
              value={newSourceWord}
              onChange={(e) => setNewSourceWord(e.target.value)}
              placeholder="Enter source word or phrase"
            />
          </div>
          <div style={{ marginBottom: 15 }}>
            <label style={{ display: 'block', marginBottom: 5 }}>
              <strong>Target Word/Phrase:</strong>
            </label>
            <Input
              value={newTargetWord}
              onChange={(e) => setNewTargetWord(e.target.value)}
              placeholder="Enter target word or phrase"
            />
          </div>
          <Button type="primary" onClick={handleAddWord} icon={<PlusOutlined />}>
            Add Word/Phrase Pair
          </Button>
        </div>

        <div style={{ background: '#f0f7ff', padding: 15, borderRadius: 6 }}>
          <div style={{ marginBottom: 12 }}>
            <strong>Current Always Translate Pairs:</strong>
            <Tooltip title="The AI will always translate these source words/phrases to their specified target equivalents">
              <QuestionCircleOutlined style={{ marginLeft: 5, color: '#1677ff' }} />
            </Tooltip>
          </div>

          <div style={{ maxHeight: 300, overflowY: 'auto', padding: '5px 0' }}>
            {currentLanguagePair && (alwaysTranslateWords[currentLanguagePair] ?? []).length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', margin: '0 -5px' }}>
                {(alwaysTranslateWords[currentLanguagePair] ?? []).map((pair, index) => {
                  const [src, tgt] = (pair || '').split(' | ').map((s) => (s ?? '').trim());
                  const isEditing = editingIndex === index;
                  return (
                    <div key={index} style={{ width: isEditing ? '100%' : '50%', padding: '0 5px', boxSizing: 'border-box', marginBottom: 10 }}>
                      <Tag style={{ padding: '6px 8px', fontSize: 14, display: 'flex', alignItems: 'center', width: '100%' }}>
                        {isEditing ? (
                          <>
                            <Input
                              value={editedSourceWord}
                              onChange={(e) => setEditedSourceWord(e.target.value)}
                              style={{ width: 150, marginRight: 8 }}
                            />
                            <span style={{ margin: '0 5px' }}>→</span>
                            <Input
                              value={editedTargetWord}
                              onChange={(e) => setEditedTargetWord(e.target.value)}
                              style={{ width: 150, marginRight: 8 }}
                            />
                            <Tooltip title="Save">
                              <SaveOutlined onClick={handleSaveEdit} style={{ marginLeft: 5, cursor: 'pointer', color: '#1677ff' }} />
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{src}</span>
                            <span style={{ margin: '0 8px', flexShrink: 0 }}>→</span>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{tgt}</span>
                            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, flexShrink: 0 }}>
                              <Tooltip title="Edit">
                                <EditOutlined onClick={() => handleEditWord(index)} style={{ cursor: 'pointer', color: '#1677ff' }} />
                              </Tooltip>
                              <Tooltip title="Delete">
                                <DeleteOutlined onClick={() => handleRemoveWord(index)} style={{ cursor: 'pointer', color: '#ff4d4f' }} />
                              </Tooltip>
                            </div>
                          </>
                        )}
                      </Tag>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ color: '#999', padding: '10px 0' }}>No translation pairs added yet. Add your first pair above.</div>
            )}
          </div>
        </div>
      </Spin>
    </Modal>
  );
};

export default DemoAlwaysTranslateModal;
